import React from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";

import { graphqlEndpoint } from "../../config";
import { CREATE_PLAYBOOK } from "../../graph/playbooks/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const validationSchema = yup.object({
  name: yup.string().trim().required("Playbook name is required"),
  objective: yup.string().nullable(),
  description: yup.string().nullable(),
});

const CreatePlaybookForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const redirectPath = location.state?.redirectPath || "/playbooks";

  const initialValues = {
    name: "",
    objective: "",
    description: "",
  };

  const handleSubmit = async (values) => {
    if (!tenantId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Select a tenant before creating a playbook",
      });
      return;
    }

    const input = {
      tenantId,
      name: values.name.trim(),
      objective: values.objective?.trim() || null,
      description: values.description?.trim() || null,
    };

    try {
      await client.request(CREATE_PLAYBOOK, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Playbook created successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Failed to create playbook:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to create playbook",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="CREATE PLAYBOOK"
          subtitle="Select a tenant before creating a playbook"
        />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="CREATE PLAYBOOK"
        subtitle={`Create playbook for ${tenantName || "selected tenant"}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Playbook Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                label="Objective"
                name="objective"
                value={values.objective}
                onChange={handleChange}
                onBlur={handleBlur}
                multiline
                rows={2}
                error={!!touched.objective && !!errors.objective}
                helperText={touched.objective && errors.objective}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Create Playbook
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreatePlaybookForm;